/**
 * Macro shape evaluator — pure value(t, shape) function.
 *
 * `t` is normalized progress along the lane, 0..1 inclusive.
 * Returns a normalized value 0..1 (after warps applied).
 *
 * Bridge and PWA both import this so the editor preview and the runtime
 * agree on what each shape looks like, byte-for-byte.
 */

import type { MacroShape } from './macros.js';

const TAU = Math.PI * 2;

function applySkew(t: number, skew: number): number {
  if (!skew || skew === 0) return t;
  // skew in -1..1. Maps via t^(2^skew):
  //   skew=0  → exponent 1 (linear)
  //   skew=+1 → exponent 2 (weight late: 0..1 stays near 0 longer)
  //   skew=-1 → exponent 0.5 (weight early: ramps up fast)
  const exponent = Math.pow(2, skew);
  return Math.pow(Math.max(0, Math.min(1, t)), exponent);
}

function baseValue(t: number, shape: MacroShape): number {
  const tt = Math.max(0, Math.min(1, t));
  switch (shape.kind) {
    case 'sine': {
      // half-sine arch: rises to 1 at t=0.5, back to 0 at t=1.
      return Math.sin(tt * Math.PI);
    }
    case 'exp': {
      // exponential rise from 0 to 1. e^(k*t) normalized so f(0)=0 f(1)=1.
      const k = 4;
      return (Math.exp(k * tt) - 1) / (Math.exp(k) - 1);
    }
    case 'scurve': {
      // smoothstep — symmetric S.
      return tt * tt * (3 - 2 * tt);
    }
    case 'stepped': {
      const n = Math.max(2, Math.min(16, shape.steps ?? 4));
      // n flat steps stepping up 0 → (n-1)/(n-1) = 1.
      const step = Math.floor(tt * n);
      const clamped = Math.min(n - 1, step);
      return clamped / (n - 1);
    }
    case 'ramp': {
      return tt;
    }
    case 'dip': {
      // V shape: 1 at endpoints, 0 in the middle.  Inverse of sine arch.
      return 1 - Math.sin(tt * Math.PI);
    }
    case 'hold': {
      return 1;
    }
  }
}

/**
 * Evaluate shape at t (0..1). Returns a 0..1 value with all warps applied.
 *
 * Order of operations:
 *   1. reverse  → t = 1 - t
 *   2. skew     → t = warped t
 *   3. base shape value(t)
 *   4. amplitude scale: v *= amplitude (default 1)
 *   5. offset add:      v += offset    (default 0)
 *   6. clamp 0..1
 */
export function evalShape(t: number, shape: MacroShape): number {
  let tt = Math.max(0, Math.min(1, t));
  if (shape.reverse) tt = 1 - tt;
  tt = applySkew(tt, shape.skew ?? 0);
  let v = baseValue(tt, shape);
  v *= shape.amplitude ?? 1;
  v += shape.offset ?? 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * Translate a shape's 0..1 output into a MIDI value 0..127 given the
 * lane's center + amplitude. Bridge runtime and editor preview both
 * use this so the "MIDI value at this point" readout in the editor
 * matches what the bridge will send live.
 *
 *   shape=1 → center + amplitude  (clamped 0..127)
 *   shape=0 → center - amplitude  (clamped 0..127)
 *
 * For 'hold' (shape=1 always) the lane just sits at center+amplitude.
 * Often combined with amplitude=0 if you want a constant offset.
 */
export function shapeToMidiValue(shapeValue: number, center: number, amplitude: number): number {
  // shapeValue is 0..1.  Map to (center-amplitude)..(center+amplitude).
  const raw = center - amplitude + shapeValue * (amplitude * 2);
  return Math.max(0, Math.min(127, Math.round(raw)));
}

/** Sugar: evaluate a shape AND convert to a MIDI value in one call. */
export function evalShapeMidi(t: number, shape: MacroShape, center: number, amplitude: number): number {
  return shapeToMidiValue(evalShape(t, shape), center, amplitude);
}

// =====================================================================
// "Stock" preset shape configurations
// =====================================================================

/**
 * Pre-baked shape recipes for common macro lanes. The editor exposes these
 * as one-click presets; users can then tweak warps. Each preset is just a
 * MacroShape object — there's no special preset runtime.
 */
export const MACRO_SHAPE_PRESETS = {
  /** Smooth swell up to peak and back. Riser, drum-build, FX sweep. */
  riser:   { kind: 'sine',   skew: 0.4 } satisfies MacroShape,
  /** Hard exponential pull. Drop-line ramp-up that lands on the downbeat. */
  drop:    { kind: 'exp',    skew: 0 } satisfies MacroShape,
  /** Quick stepped lift. Fill texture. */
  fill:    { kind: 'stepped', steps: 8 } satisfies MacroShape,
  /** Linear sweep. Filter cutoff opens / closes. */
  sweep:   { kind: 'ramp',   skew: 0 } satisfies MacroShape,
  /** Wobble — half-sine repeated wouldn't fit; use scurve+offset as a placeholder. */
  curve:   { kind: 'scurve', skew: 0 } satisfies MacroShape,
  /** Inverse: starts at full, dips to zero on the downbeat, returns. */
  dip:     { kind: 'dip',    skew: 0 } satisfies MacroShape,
  /** Constant value the entire length — set amplitude=0 to send center value as a single event. */
  hold:    { kind: 'hold' } satisfies MacroShape,
} as const;

export type MacroShapePresetName = keyof typeof MACRO_SHAPE_PRESETS;
